import React from 'react';
import { IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useToggle } from 'usehooks-ts';

import ChevronDownIcon from '@/assets/icons/chevron-down.svg?react';
import InfoIcon from '@/assets/icons/circle-info.svg?react';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { transforms } from '@/theme/constants';

interface CollapsibleContentProps {
  children: React.ReactNode;
  title: string;
  tooltip: string;
}

const CollapsibleContent = ({ children, title, tooltip }: CollapsibleContentProps) => {
  const [isOpen, toggleOpen] = useToggle(true);

  const { spacing, typography, palette } = useTheme();

  return (
    <Stack direction="column" alignItems="start" gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton
          onClick={toggleOpen}
          sx={{
            p: 0,
            width: spacing(2.5),
            height: spacing(2.5),
            '& .ChevronDownIcon-root': {
              width: spacing(2.5),
              height: spacing(2.5),
              transform: transforms.mirrorY(isOpen)
            }
          }}
        >
          <ChevronDownIcon className="ChevronDownIcon-root" />
        </IconButton>
        <Typography
          variant="body1"
          fontWeight={typography.fontWeightMedium}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={toggleOpen}
        >
          {title}
        </Typography>
        <StandardPopper
          placement="bottom-end"
          caretEndPosition={spacing(1)}
          getSx={() => ({
            zIndex: 10000
          })}
          treeRootSelector=".MuiDialog-container"
          hoverable
          content={
            <Paper
              sx={{
                mt: 1,
                p: spacing(0.75, 1.5),
                position: 'relative',
                maxWidth: '240px',
                '.StandardPopper-root &': {
                  bgcolor: palette.primary[100]
                }
              }}
            >
              <Typography sx={typography.body3}>{tooltip}</Typography>
            </Paper>
          }
        >
          <IconButton
            sx={{
              p: 0,
              width: spacing(2),
              height: spacing(2),
              '& .InfoIcon-root': { width: spacing(2), height: spacing(2) }
            }}
          >
            <InfoIcon className="InfoIcon-root" />
          </IconButton>
        </StandardPopper>
      </Stack>
      {isOpen && children}
    </Stack>
  );
};

export default CollapsibleContent;
